import React, { useState } from 'react';
import { cn } from '@/lib/utils';
import {
  ARTEMIS_ONLINE_LEADS_PATH,
  MENU_FEATURE_FLAGS,
  menuSections,
  MenuItem,
  type MenuFeatureFlag
} from '@/config/menu-config';
import { UserProfile } from '@/services/userService';
import { useArtemisLauncher } from '@/hooks/useArtemisLauncher';
import { useFeatureFlag, type FeatureFlagState } from '@/hooks/useFeatureFlag';
import { useOnlineLeadsGate } from '@/hooks/useOnlineLeadsGate';
import { useMarketingStudioNavigation } from '@/components/marketing/useMarketingStudioNavigation';
import {
  ChevronDown,
  ChevronsUpDown,
  LogOut,
  UserCircle,
  Bell,
  HelpCircle,
  Pin,
  PinOff,
  UserCog,
  RefreshCw
} from 'lucide-react';
import { openMarketDashboard, openMarketingStudioWindow } from '@/utils/windowOpenHelper';
import {
  Sidebar as SidebarPrimitive,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar
} from '@/components/ui/sidebar';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectSeparator
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from '@/components/ui/collapsible';
const SIDEBAR_SECTION_LABELS = ['Workspace', 'Production', 'Tools', 'Company Reports'] as const;

interface User {
  email?: string;
  firstName?: string;
  fullName?: string;
  user_metadata?: {
    full_name?: string;
  };
}

interface RoleAccess {
  currentRole: {
    name: string;
  };
  hasAnyPermission: (permissions: string[]) => boolean;
  // Admin impersonation
  isAdmin?: boolean;
  isImpersonating?: boolean;
  availableUsers?: UserProfile[];
  impersonatedUser?: UserProfile | null;
  switchToUser?: (user: UserProfile | null) => void;
  refreshUsers?: () => void;
}

interface SidebarProps {
  user: User;
  signOut: () => Promise<void>;
  roleAccess: RoleAccess;
  activeItem: string;
  onSetActiveItem: (itemId: string) => void;
  /** Opens Artemis, optionally at a view such as Online Leads. */
  onOpenArtemis?: (path?: string) => void;
  isFixedMode: boolean;
  onFixedModeChange: (isFixed: boolean) => void;
  isHomeCustomizing?: boolean;
  isEffectiveAdmin?: boolean;
  loanOfficerHasAnyPermission?: (permissions: string[]) => boolean;
  isLoadingLoanOfficerPermissions?: boolean;
}

export const Sidebar: React.FC<SidebarProps> = ({
  user,
  signOut,
  roleAccess,
  activeItem,
  onSetActiveItem,
  onOpenArtemis,
  isFixedMode,
  onFixedModeChange,
  isHomeCustomizing = false,
  isEffectiveAdmin = false,
  loanOfficerHasAnyPermission = () => false,
  isLoadingLoanOfficerPermissions = false
}) => {
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const { state } = useSidebar();
  const onlineLeadsGate = useOnlineLeadsGate();
  const pickupRepriceOpportunitiesFlag = useFeatureFlag(
    MENU_FEATURE_FLAGS.pickupRepriceOpportunities
  );
  const marketingStudioFlag = useFeatureFlag(
    MENU_FEATURE_FLAGS.marketingStudio
  );
  const myEmailNotificationsFlag = useFeatureFlag(
    MENU_FEATURE_FLAGS.myEmailNotifications
  );
  const marketingNavigationAllowed = useMarketingStudioNavigation(
    user?.email || 'anonymous',
    !marketingStudioFlag.loading && marketingStudioFlag.enabled
      && roleAccess.hasAnyPermission(['manage_marketing_studio', 'use_marketing_studio']),
    roleAccess.hasAnyPermission(['manage_marketing_studio'])
  );
  const featureFlagStates: Record<MenuFeatureFlag, FeatureFlagState> = {
    [MENU_FEATURE_FLAGS.onlineLeads]: { loading: onlineLeadsGate.loading, enabled: onlineLeadsGate.enabled },
    [MENU_FEATURE_FLAGS.pickupRepriceOpportunities]: pickupRepriceOpportunitiesFlag,
    [MENU_FEATURE_FLAGS.marketingStudio]: { ...marketingStudioFlag, enabled: marketingNavigationAllowed },
    [MENU_FEATURE_FLAGS.myEmailNotifications]: myEmailNotificationsFlag
  };
  const isCollapsed = state === 'collapsed';
  const userDisplayName = user?.fullName
    || user?.user_metadata?.full_name
    || user?.email?.split('@')[0]
    || 'User';
  const userInitials = userDisplayName
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part.charAt(0).toUpperCase())
    .join('') || 'U';
  const userKey = user?.email || 'anonymous';
  const fallbackOpenArtemis = useArtemisLauncher({
    enabled: !onOpenArtemis,
    userKey
  });

  const toggleExpanded = (itemId: string) => {
    setExpandedItems(prev =>
      prev.includes(itemId)
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };

  const handleItemClick = (itemId: string, hasSubItems: boolean) => {
    // Handle Market Watch as special case - opens external window
    if (itemId === 'market-watch') {
      openMarketDashboard();
      return;
    }

    // Handle Artemis - opens protected standalone internal page
    if (itemId === 'artemis') {
      (onOpenArtemis ?? fallbackOpenArtemis)();
      return;
    }

    // Online Leads lives inside Artemis: open the Artemis tab on that lane.
    if (itemId === 'artemis-online-leads') {
      (onOpenArtemis ?? fallbackOpenArtemis)(ARTEMIS_ONLINE_LEADS_PATH);
      return;
    }

    if (itemId === 'marketing') {
      openMarketingStudioWindow();
      return;
    }

    // Handle Bat Cave - navigates to standalone page
    if (itemId === 'batcave') {
      window.location.href = '/batcave-admin';
      return;
    }

    onSetActiveItem(itemId);
    if (hasSubItems) {
      toggleExpanded(itemId);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const isMenuFeatureEnabled = (featureFlag?: MenuFeatureFlag): boolean => {
    if (!featureFlag) {
      return true;
    }
    const flagState = featureFlagStates[featureFlag];
    return !flagState.loading && flagState.enabled;
  };

  const isMenuFeatureHidden = (featureFlag?: MenuFeatureFlag): boolean => {
    if (!featureFlag) {
      return false;
    }
    const flagState = featureFlagStates[featureFlag];
    // Hide the rollback entry while the flag resolves so both menu homes can
    // never flash at once.
    return flagState.loading || flagState.enabled;
  };

  // Filter menu items based on feature availability and permissions.
  const filterMenuItems = (items: MenuItem[]): MenuItem[] => {
    return items.filter(item => {
      if (!isMenuFeatureEnabled(item.featureFlag)) {
        return false;
      }
      if (isMenuFeatureHidden(item.hideWhenFeatureEnabled)) {
        return false;
      }
      if (!item.permissions || item.permissions.length === 0) {
        return true;
      }
      return roleAccess.hasAnyPermission(item.permissions);
    }).map(item => ({
      ...item,
      subItems: item.subItems?.filter(subItem => {
        if (!isMenuFeatureEnabled(subItem.featureFlag)) {
          return false;
        }
        if (isMenuFeatureHidden(subItem.hideWhenFeatureEnabled)) {
          return false;
        }
        if (!subItem.permissions || subItem.permissions.length === 0) {
          return true;
        }
        return roleAccess.hasAnyPermission(subItem.permissions);
      })
    }));
  };

  const filteredMenuSections = menuSections.map((section, sectionIndex) => ({
    ...section,
    shellLabel: SIDEBAR_SECTION_LABELS[sectionIndex] ?? section.title,
    items: filterMenuItems(section.items)
  })).filter(section => section.items.length > 0);
  const activeParentId = filteredMenuSections
    .flatMap(section => section.items)
    .find(item => item.subItems?.some(sub => sub.id === activeItem))?.id;

  // Auto-expand the visible parent of the active child. Pick Up Analysis has
  // two configured parents for flag rollback, but exactly one is visible.
  React.useEffect(() => {
    if (activeParentId) {
      setExpandedItems(prev => (
        prev.includes(activeParentId) ? prev : [...prev, activeParentId]
      ));
    }
  }, [activeItem, activeParentId]);

  const showLoanOfficerVisibilityAudit = activeItem === 'home'
    && isHomeCustomizing
    && isEffectiveAdmin
    && !isLoadingLoanOfficerPermissions;
  const isHiddenFromLoanOfficer = (
    item: Pick<MenuItem, 'permissions'>
  ): boolean =>
    showLoanOfficerVisibilityAudit
    && !!item.permissions?.length
    && !loanOfficerHasAnyPermission(item.permissions);
  const visibilityDescriptionId = (itemId: string) =>
    `loan-officer-visibility-${itemId}`;
  const renderAdminTag = (itemId: string) => (
    <span
      id={visibilityDescriptionId(itemId)}
      className="lw-shell-sidebar__admin-tag"
    >
      Admin
    </span>
  );

  return (
    <SidebarPrimitive
      collapsible="icon"
      className="lw-shell-sidebar border-r border-white/10"
    >
        {/* Header with Logo */}
        <SidebarHeader className="lw-ds lw-shell-sidebar__section lw-shell-sidebar__header">
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton
                size="lg"
                className="lw-shell-sidebar__brand data-[state=open]:bg-transparent hover:bg-transparent"
                aria-label="LendWise portal"
              >
                <div className="lw-shell-sidebar__brand-full">
                  <span className="lw-shell-sidebar__wordmark">LendWise</span>
                  <span className="lw-shell-sidebar__tagline">Mortgage Platform</span>
                </div>
                <span className="lw-shell-sidebar__brand-mark" aria-hidden="true">LW</span>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarHeader>

        {/* Main Navigation - Auto-scrollable */}
        <SidebarContent
          className="lw-ds lw-shell-sidebar__section lw-shell-sidebar__content"
          role="navigation"
          aria-label="Portal navigation"
        >
          {filteredMenuSections.map((section, sectionIndex) => (
            <React.Fragment key={sectionIndex}>
              <SidebarGroup className="lw-shell-sidebar__group">
                <SidebarGroupLabel className="lw-shell-sidebar__group-label">
                  {section.shellLabel}
                </SidebarGroupLabel>
                <SidebarGroupContent>
                  <SidebarMenu className="lw-shell-sidebar__menu">
                    {section.items.map((item) => {
                      const Icon = item.icon;
                      const isActive = activeItem === item.id;
                      const isExpanded = expandedItems.includes(item.id);
                      const hasSubItems = !!(item.subItems && item.subItems.length > 0);
                      const childActive = hasSubItems && (item.subItems?.some(sub => sub.id === activeItem) ?? false);
                      const hiddenFromLoanOfficer = isHiddenFromLoanOfficer(item);

                      if (hasSubItems) {
                        return (
                          <Collapsible
                            key={item.id}
                            asChild
                            open={isExpanded}
                            onOpenChange={() => toggleExpanded(item.id)}
                          >
                            <SidebarMenuItem>
                              <CollapsibleTrigger asChild>
                                <SidebarMenuButton
                                  isActive={isActive || childActive}
                                  tooltip={item.title}
                                  className="lw-shell-sidebar__item"
                                  aria-current={isActive ? 'page' : undefined}
                                  aria-label={hiddenFromLoanOfficer ? item.title : undefined}
                                  aria-describedby={
                                    hiddenFromLoanOfficer
                                      ? visibilityDescriptionId(item.id)
                                      : undefined
                                  }
                                  onClick={
                                    item.id === 'company-settings'
                                      ? () => onSetActiveItem(item.id)
                                      : undefined
                                  }
                                >
                                  <Icon className="h-4 w-4" />
                                  <span>{item.title}</span>
                                  {hiddenFromLoanOfficer && renderAdminTag(item.id)}
                                  {item.badge && (
                                    <Badge
                                      variant="secondary"
                                      className={cn(
                                        "h-5 px-1.5 text-xs",
                                        !hiddenFromLoanOfficer && "ml-auto"
                                      )}
                                    >
                                      {item.badge}
                                    </Badge>
                                  )}
                                  {!isExpanded && (
                                    <span className="lw-shell-sidebar__subcount" aria-hidden="true">
                                      {item.subItems?.length}
                                    </span>
                                  )}
                                  <ChevronDown
                                    className={cn(
                                      "lw-shell-sidebar__chevron h-3 w-3 transition-transform",
                                      !hiddenFromLoanOfficer && "ml-auto",
                                      isExpanded && "rotate-180"
                                    )}
                                  />
                                </SidebarMenuButton>
                              </CollapsibleTrigger>
                              <CollapsibleContent>
                                <SidebarMenuSub>
                                  {item.subItems?.map((subItem) => {
                                    const childHiddenFromLoanOfficer =
                                      isHiddenFromLoanOfficer(subItem);
                                    return (
                                      <SidebarMenuSubItem key={subItem.id}>
                                        <SidebarMenuSubButton
                                          onClick={() => onSetActiveItem(subItem.id)}
                                          isActive={activeItem === subItem.id}
                                          className="lw-shell-sidebar__subitem"
                                          aria-current={
                                            activeItem === subItem.id ? 'page' : undefined
                                          }
                                          aria-label={
                                            childHiddenFromLoanOfficer
                                              ? subItem.title
                                              : undefined
                                          }
                                          aria-describedby={
                                            childHiddenFromLoanOfficer
                                              ? visibilityDescriptionId(subItem.id)
                                              : undefined
                                          }
                                        >
                                          <span>{subItem.title}</span>
                                          {childHiddenFromLoanOfficer
                                            && renderAdminTag(subItem.id)}
                                        </SidebarMenuSubButton>
                                      </SidebarMenuSubItem>
                                    );
                                  })}
                                </SidebarMenuSub>
                              </CollapsibleContent>
                            </SidebarMenuItem>
                          </Collapsible>
                        );
                      }

                      return (
                        <SidebarMenuItem key={item.id}>
                          <SidebarMenuButton
                            isActive={isActive}
                            tooltip={item.title}
                            onClick={() => handleItemClick(item.id, hasSubItems)}
                            className="lw-shell-sidebar__item"
                            aria-current={isActive ? 'page' : undefined}
                            aria-label={hiddenFromLoanOfficer ? item.title : undefined}
                            aria-describedby={
                              hiddenFromLoanOfficer
                                ? visibilityDescriptionId(item.id)
                                : undefined
                            }
                          >
                            <Icon className="h-4 w-4" />
                            <span>{item.title}</span>
                            {hiddenFromLoanOfficer && renderAdminTag(item.id)}
                            {item.badge && (
                              <Badge
                                variant="secondary"
                                className={cn(
                                  "h-5 px-1.5 text-xs",
                                  !hiddenFromLoanOfficer && "ml-auto"
                                )}
                              >
                                {item.badge}
                              </Badge>
                            )}
                          </SidebarMenuButton>
                        </SidebarMenuItem>
                      );
                    })}
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
            </React.Fragment>
          ))}
        </SidebarContent>

        {/* Footer */}
        <SidebarFooter className="lw-ds lw-shell-sidebar__section lw-shell-sidebar__footer">
          {/* Admin User Impersonation */}
          {roleAccess.isAdmin && !isCollapsed && (
            <div className="lw-shell-sidebar__impersonation mb-3 space-y-2 px-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <UserCog className="h-3 w-3 text-muted-foreground" />
                  <span className="text-xs font-medium text-muted-foreground">
                    View As User
                  </span>
                </div>
                {roleAccess.refreshUsers && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={roleAccess.refreshUsers}
                    className="h-5 w-5 p-0"
                    title="Refresh users"
                  >
                    <RefreshCw className="h-3 w-3" />
                  </Button>
                )}
              </div>

              <Select
                value={roleAccess.impersonatedUser?.id || 'self'}
                onValueChange={(value) => {
                  if (roleAccess.switchToUser) {
                    if (value === 'self') {
                      roleAccess.switchToUser(null);
                    } else {
                      const user = roleAccess.availableUsers?.find(u => u.id === value);
                      if (user) {
                        roleAccess.switchToUser(user);
                      }
                    }
                  }
                }}
              >
                <SelectTrigger className="h-8 border-white/15 bg-white/5 text-xs text-[var(--gold-soft)]">
                  <SelectValue placeholder="Select user..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="self">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">👤 Yourself</span>
                    </div>
                  </SelectItem>
                  {roleAccess.availableUsers && roleAccess.availableUsers.length > 0 && (
                    <>
                      <SelectSeparator />
                      {roleAccess.availableUsers.map((user) => (
                        <SelectItem key={user.id} value={user.id}>
                          <div className="flex flex-col">
                            <span className="font-medium text-xs">
                              {user.full_name || user.email}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {user.organization?.name || 'No Org'}
                            </span>
                          </div>
                        </SelectItem>
                      ))}
                    </>
                  )}
                </SelectContent>
              </Select>

              {roleAccess.impersonatedUser && (
                <div className="rounded bg-[var(--gold-wash)] p-1.5 text-xs text-[var(--warn-ink)]">
                  ⚠️ Viewing as {roleAccess.impersonatedUser.email}
                </div>
              )}
            </div>
          )}

          {isCollapsed ? (
            <SidebarMenu>
              {roleAccess.isAdmin && (
                <SidebarMenuItem>
                  <SidebarMenuButton
                    tooltip={roleAccess.impersonatedUser ? `Viewing as ${roleAccess.impersonatedUser.email}` : 'View As User'}
                    isActive={!!roleAccess.impersonatedUser}
                    onClick={() => onFixedModeChange(true)}
                  >
                    <UserCog className="h-4 w-4" />
                    <span>View As User</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}

              <SidebarMenuItem>
                <SidebarMenuButton
                  tooltip="Expand sidebar"
                  onClick={() => onFixedModeChange(true)}
                >
                  <PinOff className="h-4 w-4" />
                  <span>Auto</span>
                </SidebarMenuButton>
              </SidebarMenuItem>

              <SidebarMenuItem>
                <DropdownMenu modal={false}>
                  <DropdownMenuTrigger asChild>
                    <SidebarMenuButton tooltip="Account">
                    <Avatar className="size-6 rounded-lg">
                        <AvatarFallback className="rounded-lg bg-[var(--warn-ink)] text-[10px] text-[var(--lw-card)]">
                          {userInitials}
                        </AvatarFallback>
                      </Avatar>
                      <span>Account</span>
                    </SidebarMenuButton>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent sideOffset={5} className="lw-ds w-56">
                    <div className="flex flex-row items-center gap-3 p-3">
                      <Avatar className="size-8">
                        <AvatarFallback className="bg-primary text-primary-foreground">
                          {userInitials}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex flex-col text-left">
                        <span className="text-sm font-medium">{userDisplayName}</span>
                        <span className="text-xs text-muted-foreground">{user?.email || 'user@lendwisemtg.com'}</span>
                      </div>
                    </div>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem className="flex items-center gap-2">
                      <UserCircle className="h-4 w-4" />
                      Profile
                    </DropdownMenuItem>
                    <DropdownMenuItem className="flex items-center gap-2">
                      <Bell className="h-4 w-4" />
                      Notifications
                    </DropdownMenuItem>
                    <DropdownMenuItem className="flex items-center gap-2">
                      <HelpCircle className="h-4 w-4" />
                      Help & Support
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      className="flex items-center gap-2 text-red-600"
                      onClick={handleLogout}
                    >
                      <LogOut className="h-4 w-4" />
                      Sign out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </SidebarMenuItem>
            </SidebarMenu>
          ) : (
            <>
              {/* Sidebar Mode Toggle */}
              <div className="lw-shell-sidebar__mode mb-2 flex items-center justify-between px-2">
                <div className="flex items-center space-x-2">
                  {isFixedMode ? (
                    <Pin className="h-3 w-3 text-muted-foreground" />
                  ) : (
                    <PinOff className="h-3 w-3 text-muted-foreground" />
                  )}
                  <span className="text-xs text-muted-foreground">
                    {isFixedMode ? 'Fixed' : 'Auto'}
                  </span>
                </div>
                <Switch
                  checked={isFixedMode}
                  onCheckedChange={onFixedModeChange}
                  className="scale-75"
                  aria-label="Keep sidebar expanded"
                />
              </div>

              {/* User Profile Dropdown */}
              <DropdownMenu modal={false}>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="lw-shell-sidebar__account h-auto w-full justify-start p-2">
                    <div className="flex w-full items-center gap-3">
                      <Avatar className="size-7 rounded-lg">
                        <AvatarFallback className="rounded-lg bg-[var(--warn-ink)] text-xs text-[var(--lw-card)]">
                          {userInitials}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex min-w-0 flex-1 flex-col items-start">
                        <span className="w-full truncate text-sm font-medium">
                          {userDisplayName}
                        </span>
                        <span className="w-full truncate text-xs text-muted-foreground">
                          {roleAccess.currentRole.name}
                        </span>
                      </div>
                      <kbd className="lw-shell-sidebar__shortcut" aria-label="Command or Control B collapses the sidebar">
                        ⌘B
                      </kbd>
                      <ChevronsUpDown className="h-3 w-3 shrink-0 opacity-40" />
                    </div>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent sideOffset={5} className="lw-ds w-56">
                  <div className="flex flex-row items-center gap-3 p-3">
                    <Avatar className="size-8">
                      <AvatarFallback className="bg-primary text-primary-foreground">
                        {userInitials}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex flex-col text-left">
                      <span className="text-sm font-medium">{userDisplayName}</span>
                      <span className="text-xs text-muted-foreground">{user?.email || 'user@lendwisemtg.com'}</span>
                    </div>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem className="flex items-center gap-2">
                    <UserCircle className="h-4 w-4" />
                    Profile
                  </DropdownMenuItem>
                  <DropdownMenuItem className="flex items-center gap-2">
                    <Bell className="h-4 w-4" />
                    Notifications
                  </DropdownMenuItem>
                  <DropdownMenuItem className="flex items-center gap-2">
                    <HelpCircle className="h-4 w-4" />
                    Help & Support
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    className="flex items-center gap-2 text-red-600"
                    onClick={handleLogout}
                  >
                    <LogOut className="h-4 w-4" />
                    Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          )}
        </SidebarFooter>
    </SidebarPrimitive>
  );
};
